import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  ReactFlowProvider,
  Panel,
  Position,
} from '@xyflow/react';
import dagre from 'dagre';

import { nodeTypes } from './CustomNode';
import { edgeTypes } from './CustomEdge';
import { NodeToolbar } from './NodeToolbar';
import type { 
  ReleaseFlowDto, 
  ReactFlowNode, 
  ReactFlowEdge,
  ReleaseFlowNodeDto,
  ReleaseFlowEdgeDto
} from '../../types/releaseFlow';

import '@xyflow/react/dist/style.css';

interface ReleaseFlowEditorProps {
  releaseFlow?: ReleaseFlowDto;
  readOnly?: boolean;
  onSave?: (data: { nodes: ReleaseFlowNodeDto[]; edges: ReleaseFlowEdgeDto[] }) => void;
  onValidate?: () => void;
  className?: string;
}

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'TB') => {
  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 200, height: 80 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = isHorizontal ? Position.Left : Position.Top;
    node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;
    node.position = {
      x: nodeWithPosition.x - 100,
      y: nodeWithPosition.y - 40,
    };
  });

  return { nodes, edges };
};

const convertToReactFlowNodes = (
  nodes: ReleaseFlowNodeDto[], 
  releaseFlowId?: string, 
  readOnly: boolean = false
): ReactFlowNode[] => {
  return nodes.map((node) => {
    // Convert single nodePipelineLink to pipelineAssociations array format
    const pipelineAssociations = node.nodePipelineLink ? [node.nodePipelineLink] : [];
    
    return {
      id: node.id,
      type: 'custom',
      position: { x: node.positionX, y: node.positionY },
      data: {
        label: node.name,
        nodeType: node.nodeType as any,
        pipelineId: node.nodePipelineLink?.pipelineDefinitionId?.toString(),
        pipelineName: node.nodePipelineLink?.pipelineName,
        configuration: node.configuration,
        description: node.description,
        isActive: node.isActive,
        backgroundColor: node.backgroundColor,
        borderColor: node.borderColor,
        width: node.width,
        height: node.height,
        pipelineAssociations: pipelineAssociations, // Convert nodePipelineLink to array format
        releaseFlowId, // Pass the release flow ID
        readOnly, // Pass read-only state
      },
    };
  });
};

const convertToReactFlowEdges = (edges: ReleaseFlowEdgeDto[]): ReactFlowEdge[] => {
  return edges.map((edge) => ({
    id: edge.id,
    source: edge.sourceNodeId,
    target: edge.targetNodeId,
    type: edge.edgeType,
    label: edge.label,
    animated: edge.animated,
    style: edge.style ? JSON.parse(edge.style) : undefined,
    markerEnd: edge.markerEnd ? { type: edge.markerEnd } : { type: 'arrowclosed' },
    data: {
      condition: edge.condition,
      orderIndex: edge.orderIndex,
      isActive: edge.isActive,
    },
  }));
};

const convertFromReactFlowNodes = (nodes: Node[]): ReleaseFlowNodeDto[] => {
  return nodes.map((node) => ({
    id: node.id,
    releaseFlowId: '', // Will be set by parent
    name: (node.data.label as string) || '',
    description: (node.data.description as string) || '',
    nodeType: (node.data.nodeType as string) || '',
    pipelineId: (node.data.pipelineId as string) || '',
    pipelineName: (node.data.pipelineName as string) || '',
    configuration: (node.data.configuration as string) || '{}',
    isActive: (node.data.isActive as boolean) ?? true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    positionX: node.position.x,
    positionY: node.position.y,
    width: String((node.data.width as number) || 200),
    height: String((node.data.height as number) || 80),
    backgroundColor: (node.data.backgroundColor as string) || '',
    borderColor: (node.data.borderColor as string) || '',
    incomingNodeIds: [], // Will be calculated from edges
    outgoingNodeIds: [], // Will be calculated from edges
  }));
};

const convertFromReactFlowEdges = (edges: Edge[]): ReleaseFlowEdgeDto[] => {
  return edges.map((edge, index) => ({
    id: edge.id,
    releaseFlowId: '', // Will be set by parent
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    condition: (edge.data?.condition as string) || undefined,
    orderIndex: (edge.data?.orderIndex as number) || index,
    isActive: (edge.data?.isActive as boolean) ?? true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    edgeType: edge.type || 'smoothstep',
    label: (edge.label as string) || '',
    animated: edge.animated || false,
    style: edge.style ? JSON.stringify(edge.style) : undefined,
    markerEnd: typeof edge.markerEnd === 'object' ? (edge.markerEnd as any)?.type || 'arrowclosed' : edge.markerEnd || 'arrowclosed',
    sourceNodeName: '', // Will be populated by API
    targetNodeName: '', // Will be populated by API
  }));
};

export const ReleaseFlowEditor: React.FC<ReleaseFlowEditorProps> = ({
  releaseFlow,
  readOnly = false,
  onSave,
  onValidate,
  className,
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);

  // Initialize nodes and edges from releaseFlow prop
  useEffect(() => {
    if (releaseFlow) {
      const flowNodes = convertToReactFlowNodes(
        releaseFlow.nodes, 
        releaseFlow.id, 
        readOnly || false
      );
      const flowEdges = convertToReactFlowEdges(releaseFlow.edges);
      
      if (flowNodes.length > 0) {
        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
          flowNodes,
          flowEdges as Edge[]
        );
        setNodes(layoutedNodes);
        setEdges(layoutedEdges as Edge[]);
        
        // Auto-fit view after nodes are set
        if (reactFlowInstance && layoutedNodes.length > 0) {
          setTimeout(() => {
            reactFlowInstance.fitView({ padding: 0.1, duration: 800 });
          }, 100);
        }
      }
    }
  }, [releaseFlow, setNodes, setEdges, reactFlowInstance]);

  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return;
      
      const newEdge: Edge = {
        id: `edge-${params.source}-${params.target}-${Date.now()}`,
        source: params.source!,
        target: params.target!,
        type: 'custom',
        markerEnd: { type: 'arrowclosed' },
        data: {
          condition: '',
          orderIndex: edges.length,
          isActive: true,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [edges.length, readOnly, setEdges]
  );

  const onNodesChangeHandler: OnNodesChange = useCallback(
    (changes) => {
      if (readOnly) return;
      onNodesChange(changes);
      
      // Track selected nodes
      const selectedNodeIds = changes
        .filter((change): change is any => change.type === 'select' && 'selected' in change && change.selected)
        .map((change) => (change as any).id)
        .filter(Boolean);
      
      if (selectedNodeIds.length > 0) {
        setSelectedNodes(nodes.filter((node) => selectedNodeIds.includes(node.id)));
      }
    },
    [readOnly, onNodesChange, nodes]
  );

  const onEdgesChangeHandler: OnEdgesChange = useCallback(
    (changes) => {
      if (readOnly) return;
      onEdgesChange(changes);
    },
    [readOnly, onEdgesChange]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      const nodeData = convertFromReactFlowNodes(nodes);
      const edgeData = convertFromReactFlowEdges(edges);
      onSave({ nodes: nodeData, edges: edgeData });
    }
  }, [nodes, edges, onSave]);

  const handleAutoLayout = useCallback(() => {
    if (readOnly) return;
    
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges,
      'TB'
    );
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  }, [nodes, edges, readOnly, setNodes, setEdges]);

  const handleAddNode = useCallback(
    (nodeType: string) => {
      if (readOnly) return;
      
      const newNode: Node = {
        id: crypto.randomUUID(),
        type: 'custom',
        position: { x: Math.random() * 500, y: Math.random() * 300 },
        data: {
          label: `New ${nodeType}`,
          nodeType,
          isActive: true,
          description: '',
          configuration: '{}',
        },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [readOnly, setNodes]
  );

  const handleDeleteSelected = useCallback(() => {
    if (readOnly) return;
    
    const selectedNodeIds = nodes.filter((node) => node.selected).map((node) => node.id);
    const selectedEdgeIds = edges.filter((edge) => edge.selected).map((edge) => edge.id);
    
    setNodes((nds) => nds.filter((node) => !selectedNodeIds.includes(node.id)));
    setEdges((eds) => eds.filter((edge) => 
      !selectedEdgeIds.includes(edge.id) && 
      !selectedNodeIds.includes(edge.source) && 
      !selectedNodeIds.includes(edge.target)
    ));
  }, [nodes, edges, readOnly, setNodes, setEdges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (readOnly) return;
      
      if ((event.key === 'Delete' || event.key === 'Backspace') && !event.repeat) {
        handleDeleteSelected();
      }
      
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 's':
            event.preventDefault();
            handleSave();
            break;
          case 'l':
            event.preventDefault();
            handleAutoLayout();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [readOnly, handleDeleteSelected, handleSave, handleAutoLayout]);

  return (
    <div className={`w-full h-full ${className}`} ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChangeHandler}
        onEdgesChange={onEdgesChangeHandler}
        onConnect={onConnect}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        deleteKeyCode={null} // Handle delete manually
      >
        <Background />
        <Controls />
        <MiniMap 
          nodeColor={(node) => {
            return (node.data.backgroundColor as string) || '#f3f4f6';
          }}
          maskColor="rgb(240, 242, 247, 0.7)"
        />
        
        {!readOnly && (
          <>
            <Panel position="top-left" className="m-4">
              <NodeToolbar 
                onAddNode={handleAddNode}
                onAutoLayout={handleAutoLayout}
                onSave={handleSave}
                onValidate={onValidate}
                selectedNodes={selectedNodes}
              />
            </Panel>
            
            <Panel position="bottom-right" className="m-4">
              <div className="bg-white rounded-lg shadow-lg p-3">
                <div className="text-xs text-gray-600 space-y-1">
                  <div><kbd>Ctrl+S</kbd> Save</div>
                  <div><kbd>Ctrl+L</kbd> Auto Layout</div>
                  <div><kbd>Del</kbd> Delete Selected</div>
                  <div>Drag to connect nodes</div>
                </div>
              </div>
            </Panel>
          </>
        )}
      </ReactFlow>
    </div>
  );
};

export const ReleaseFlowEditorWithProvider: React.FC<ReleaseFlowEditorProps> = (props) => (
  <ReactFlowProvider>
    <ReleaseFlowEditor {...props} />
  </ReactFlowProvider>
);